import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Ticket, CreditCard, LogOut, Check } from "lucide-react";
import { useUser } from "../store/userAuth";
import { userApi, fetchPublicContent, mediaUrl, API } from "../lib/api";

const field = "w-full bg-transparent border-b eff-line py-2.5 text-[13px] placeholder:text-[#5a5a5a] focus:outline-none focus:border-white transition-colors duration-300";

function AuthPanel() {
  const { register, login } = useUser();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ email: "", password: "", name: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setErr("");
    try {
      if (mode === "login") await login(f.email, f.password);
      else await register(f.email, f.password, f.name);
    } catch (e2) { setErr(e2.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/access";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="max-w-sm mx-auto pt-40 px-7">
      <div className="eff-eyebrow mb-4">EFFERVESCENCE Access</div>
      <h1 className="font-display text-4xl mb-8">{mode === "login" ? "Se connecter" : "Créer un compte"}</h1>
      <form data-testid="user-auth-form" onSubmit={submit} className="space-y-5">
        {mode === "register" && <input data-testid="ua-name" className={field} placeholder="Nom" value={f.name} onChange={set("name")} />}
        <input data-testid="ua-email" type="email" required className={field} placeholder="Email" value={f.email} onChange={set("email")} />
        <input data-testid="ua-password" type="password" required className={field} placeholder="Mot de passe" value={f.password} onChange={set("password")} />
        {err && <div data-testid="ua-error" className="text-[12px] text-white">{err}</div>}
        <button data-testid="ua-submit" disabled={loading} className="eff-btn eff-btn-light w-full disabled:opacity-40">
          {loading ? "..." : mode === "login" ? "Connexion" : "Inscription"}
        </button>
      </form>
      <button data-testid="ua-google" onClick={google} className="eff-btn w-full mt-3">Continuer avec Google</button>
      <button className="text-[11px] text-[#888] hover:text-white mt-6 tracking-widest uppercase"
        onClick={() => setMode(mode === "login" ? "register" : "login")}>
        {mode === "login" ? "Pas de compte ? Créer un compte" : "Déjà un compte ? Se connecter"}
      </button>
    </div>
  );
}

function Dashboard() {
  const { user, logout } = useUser();
  const [tab, setTab] = useState("tickets");
  const [tickets, setTickets] = useState([]);
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [plans, setPlans] = useState([]);

  const load = async () => {
    const [t, o, s, c] = await Promise.all([
      userApi.get("/user/tickets"), userApi.get("/user/orders"),
      userApi.get("/user/subscriptions"), fetchPublicContent(),
    ]);
    setTickets(t.data); setOrders(o.data); setSubs(s.data); setPlans(c.subscription_plans || []);
  };
  useEffect(() => { load(); }, []);

  const subscribe = async (planId) => {
    try {
      const { data } = await userApi.post("/payments/checkout/subscription", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const cancelSub = async (id) => {
    if (!window.confirm("Annuler cet abonnement ?")) return;
    await userApi.post(`/user/subscriptions/${id}/cancel`); toast.success("Abonnement annulé"); load();
  };

  const tabs = [["tickets", "Mes billets"], ["subs", "Abonnements"], ["orders", "Historique"]];
  const money = (a, c) => `${Number(a || 0).toFixed(2)} ${(c || "cad").toUpperCase()}`;

  return (
    <div className="max-w-[1180px] mx-auto px-7 pt-32 pb-24">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="eff-eyebrow mb-2">EFFERVESCENCE Access</div>
          <h1 className="font-display text-4xl">Bonjour, {user?.name}</h1>
        </div>
        <button data-testid="user-logout" onClick={logout} className="eff-btn inline-flex items-center gap-2"><LogOut className="w-3.5 h-3.5" /> Déconnexion</button>
      </div>

      <div className="flex gap-7 border-b eff-line pb-4 mb-10">
        {tabs.map(([k, l]) => (
          <button key={k} data-testid={`access-tab-${k}`} onClick={() => setTab(k)}
            className={`text-[10px] tracking-[0.2em] uppercase transition-colors ${tab === k ? "text-white" : "text-[#777] hover:text-white"}`}>{l}</button>
        ))}
      </div>

      {tab === "tickets" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {tickets.length === 0 && <p className="text-[#888] text-sm">Aucun billet pour l'instant.</p>}
          {tickets.map((t) => (
            <div key={t.id} data-testid={`ticket-${t.id}`} className="border eff-line p-6">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#888]">{t.status === "valid" ? "Valide" : t.status}</div>
              <div className="font-display text-xl mt-1 mb-4">{t.event_title}</div>
              <img src={mediaUrl(t.qr_url)} alt="QR" className="w-40 h-40 bg-white p-2" />
              <a data-testid={`ticket-pdf-${t.id}`} href={`${API}/tickets/${t.qr_token}/pdf`} target="_blank" rel="noreferrer"
                className="eff-btn mt-4 inline-block text-center">Télécharger le billet (PDF)</a>
              <div className="text-[10px] text-[#666] mt-3 break-all">{t.qr_token}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "subs" && (
        <div>
          {subs.length > 0 && (
            <div className="mb-12">
              <div className="eff-label mb-5">Vos abonnements</div>
              {subs.map((s) => (
                <div key={s.id} data-testid={`sub-${s.id}`} className="border eff-line p-6 mb-4 flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <div className="font-display text-2xl">{s.plan_name}</div>
                    <div className="text-[12px] text-[#888] mt-1 uppercase tracking-widest">{s.status}</div>
                    {s.current_period_end && (
                      <div data-testid={`sub-renew-${s.id}`} className="text-[11px] text-[#888] mt-1">
                        {s.status === "canceled" ? "Accès jusqu'au " : "Prochain renouvellement le "}
                        {new Date(s.current_period_end).toLocaleDateString("fr-CA")}
                      </div>
                    )}
                  </div>
                  {s.status === "active" && <button data-testid={`sub-cancel-${s.id}`} onClick={() => cancelSub(s.id)} className="eff-btn">Annuler</button>}
                </div>
              ))}
            </div>
          )}
          <div className="eff-label mb-5">Niveaux d'abonnement</div>
          <div className="grid md:grid-cols-3 gap-px bg-[#292929]">
            {plans.map((p) => (
              <div key={p.id} data-testid={`plan-${p.id}`} className="bg-[#080808] p-8 flex flex-col">
                <div className="font-display text-2xl">{p.name}</div>
                <div className="text-3xl font-display mt-3 mb-6">{Number(p.price_monthly).toFixed(2)} $<span className="text-[12px] text-[#888]"> /mois</span></div>
                <ul className="space-y-2 flex-1">
                  {String(p.benefits || "").split("\n").filter(Boolean).map((b, i) => (
                    <li key={i} className="flex gap-2 text-[13px] text-[#bbb]"><Check className="w-4 h-4 shrink-0 mt-0.5" /> {b}</li>
                  ))}
                </ul>
                <button data-testid={`plan-subscribe-${p.id}`} onClick={() => subscribe(p.id)} className="eff-btn eff-btn-light mt-6">S'abonner</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="border-t eff-line">
          {orders.length === 0 && <p className="text-[#888] text-sm py-6">Aucun achat.</p>}
          {orders.map((o) => (
            <div key={o.session_id} data-testid={`order-${o.session_id}`} className="border-b eff-line py-4 flex items-center justify-between gap-4">
              <div>
                <div className="text-[14px]">{o.kind === "ticket" ? `Billet · ${o.event_title || ""}` : `Abonnement · ${o.plan_name || ""}`}</div>
                <div className="text-[11px] text-[#888]">{new Date(o.created_at).toLocaleString("fr-CA")}</div>
              </div>
              <div className="text-right">
                <div className="text-[14px]">{money(o.amount, o.currency)}</div>
                <div className="text-[10px] tracking-widest uppercase text-[#888]">{o.payment_status}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AccessPage() {
  const { user, ready, refresh, completeGoogle } = useUser();
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
      setProcessing(true);
      completeGoogle(sid).catch(() => toast.error("Connexion Google échouée"))
        .finally(() => { window.history.replaceState(null, "", "/access"); setProcessing(false); });
    } else {
      refresh();
    }
    document.documentElement.classList.add("dark");
    // eslint-disable-next-line
  }, []);

  if (processing || (!ready && !user)) return <div className="min-h-screen bg-[#080808]" />;
  return (
    <div className="min-h-screen bg-[#080808]">
      <div className="max-w-[1180px] mx-auto px-7 h-[70px] flex items-center border-b eff-line">
        <a href="/" className="font-display text-lg">EFFERVESCENCE</a>
      </div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        {user ? <Dashboard /> : <AuthPanel />}
      </motion.div>
    </div>
  );
}
